// src/pages/stock/AddSupplier.tsx
//
// Add Supplier
//
// Page version of the legacy AddSupplier modal.
// Follows the same UX conventions as EditSupplier / EditStockCode.
//
// Behavior summary
// - Compact, left-aligned layout (maxWidth=900).
// - Create is enabled only when:
//   - required fields are valid,
//   - the form is dirty,
//   - and no async operation is in progress.
// - Cancel is guarded by a discard confirmation when the form is dirty.
// - Uses shared confirm modal + systemAlert for user feedback.
// - Updates AppPageHeader leading slot to show "Modified" while dirty,
//   and a short-lived "Created" hint after successful creation.
//
// Route: /stocks/suppliers/new

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import type { SupplierUpsertRequest } from '@apis/supplier';
import { createSupplier } from '@apis/supplier';

import { dropEmptyValues } from '@utils/common';
import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

const PAGE_MAX_WIDTH = 900;
const CREATED_HINT_MS = 1200;

const normalize = (v?: string | null) => (v ?? '').trim();

/**
 * Normalize only comparable fields to make dirty checks stable.
 * (e.g., ignores transient/non-editable fields and whitespace differences)
 */
const toComparable = (f: SupplierUpsertRequest) => ({
  supplierName: normalize(f.supplierName),
  managerName: normalize(f.managerName),
  email: normalize(f.email),
  phoneNumber: normalize(f.phoneNumber),
});

/**
 * Lightweight deep compare for a small, flat object.
 * Avoids extra deps and keeps intent obvious.
 */
const shallowJsonEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const AddSupplier = () => {
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const [form, setForm] = React.useState<SupplierUpsertRequest>({
    supplierName: '',
    managerName: '',
    email: '',
    phoneNumber: '',
    isDeleted: false,
  });

  // Baseline snapshot for dirty detection.
  const baselineRef = React.useRef(toComparable(form));

  const [isCreating, setIsCreating] = React.useState(false);
  const isBusy = isCreating;

  // Header hint: show "Created" briefly after a successful create.
  const [createdHintUntil, setCreatedHintUntil] = React.useState<number | null>(null);

  const showCreatedHint = React.useMemo(() => {
    return createdHintUntil !== null && Date.now() < createdHintUntil;
  }, [createdHintUntil]);

  React.useEffect(() => {
    if (!createdHintUntil) return;

    const remaining = createdHintUntil - Date.now();
    if (remaining <= 0) {
      setCreatedHintUntil(null);
      return;
    }

    const t = window.setTimeout(() => setCreatedHintUntil(null), remaining);
    return () => window.clearTimeout(t);
  }, [createdHintUntil]);

  const isValid = React.useMemo(() => {
    // Required fields: supplierName, managerName
    if (!normalize(form.supplierName)) return false;
    if (!normalize(form.managerName)) return false;
    // email / phone are optional
    return true;
  }, [form.managerName, form.supplierName]);

  const isDirty = React.useMemo(() => {
    return !shallowJsonEqual(toComparable(form), baselineRef.current);
  }, [form]);

  const canCreate = isValid && isDirty && !isBusy;

  const createDisabledReason = React.useMemo(() => {
    if (isBusy) return '';
    if (!isValid) return 'Fill in all required fields.';
    if (!isDirty) return 'No changes to save.';
    return '';
  }, [isBusy, isDirty, isValid]);

  // AppPageHeader leading slot (title + status hint)
  React.useEffect(() => {
    setPageHeaderSlots({
      leading: {
        title: 'New Supplier',
        description: isDirty ? '● Modified' : showCreatedHint ? 'Created' : undefined,
      },
    });
  }, [isDirty, setPageHeaderSlots, showCreatedHint]);

  const handleChange =
    (key: keyof SupplierUpsertRequest) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleCreate = React.useCallback(async () => {
    if (!canCreate) return;

    try {
      setIsCreating(true);

      // Keep API contract: email/phone are optional, omit when empty.
      const payload = dropEmptyValues(form, ['email', 'phoneNumber']);
      await createSupplier(payload);

      // Update baseline to reflect saved state.
      baselineRef.current = toComparable(form);

      setCreatedHintUntil(Date.now() + CREATED_HINT_MS);
      systemAlert.success('Supplier created successfully.');

      navigate('/stocks/suppliers', { replace: true });
    } catch {
      systemAlert.error('Failed to create supplier. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [canCreate, form, navigate, systemAlert]);

  const handleCancel = React.useCallback(async () => {
    if (isBusy) return;

    if (!isDirty) {
      navigate(-1);
      return;
    }

    const ok = await confirm({
      priority: 'info',
      title: 'Discard changes?',
      description: 'You have unsaved changes. If you leave now, those changes will be lost.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, isBusy, isDirty, navigate]);

  return (
    <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Supplier Name"
              value={form.supplierName}
              onChange={handleChange('supplierName')}
              required
              disabled={isBusy}
            />

            <TextField
              label="Manager"
              value={form.managerName}
              onChange={handleChange('managerName')}
              required
              disabled={isBusy}
            />

            <TextField
              label="E-mail"
              value={form.email}
              onChange={handleChange('email')}
              disabled={isBusy}
            />

            <TextField
              label="Phone"
              value={form.phoneNumber}
              onChange={handleChange('phoneNumber')}
              disabled={isBusy}
            />

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isBusy}
                startIcon={<CloseOutlinedIcon />}
                sx={{ width: { xs: '100%', sm: 140 }, whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>

              <Tooltip title={createDisabledReason} disableHoverListener={canCreate || !createDisabledReason}>
                <Box component="span" sx={{ width: { xs: '100%', sm: 140 } }}>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!canCreate}
                    startIcon={isCreating ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                    sx={{ width: '100%', whiteSpace: 'nowrap' }}
                  >
                    {isCreating ? 'Creating…' : 'Create'}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default AddSupplier;
